import React from 'react';
import { LazyAtlassianNavigation } from '@atlassian/jira-atlassian-navigation/src/async';
import { NOT_ENROLLED } from '@atlassian/jira-common-util-get-experiment-group';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { genericProjectLayout } from '@atlassian/jira-generic-project-layout';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { GENERIC_PROJECT_HORIZONTAL_NAV } from '@atlassian/jira-navigation-apps-horizontal-nav-selector/src/constants.tsx';
import { genericProjectSidebars } from '@atlassian/jira-navigation-apps-sidebar-generic-sidebars/src/ui/index.tsx';
import { createEntry } from '@atlassian/jira-route-entry';
import { getBusinessProjectResources } from '@atlassian/jira-router-resources-business-common';
import { getConsolidationStateResource } from '@atlassian/jira-router-resources-eoc-tenant-info/src/services/index.tsx';
import {
	getNavigationResources,
	getNavigationSidebarProjectResource,
} from '@atlassian/jira-router-resources-navigation';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import ErrorPagesNotFound from '@atlassian/jira-router-routes-common/src/ui/error-pages/not-found/index.tsx';
import { rovoDemandTestsRoute } from '@atlassian/jira-router-routes-rovo-demand-tests-routes';
import type RovoDemandTestsPageType from '@atlassian/jira-spa-apps-rovo-demand-tests';
import { markAsProductBundleReady } from '@atlassian/jira-spa/src/services/mark-as-product-bundle-ready';
import { LazyPage } from '@atlassian/jira-spa/src/view/lazy-page';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { Route } from '@atlassian/react-resource-router';

const getIsEnrolledInExperiment = () => {
	// Do not want to log exposure, because there are more conditions that will be checked when the component is rendered
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('jsw_rovo_demand_test');
	const isPassedGate = fg('jsw_rovo_demand_tests');
	return expConfig.get('cohort', NOT_ENROLLED) !== NOT_ENROLLED && isPassedGate;
};

export const LazyRovoDemandTestsPage = lazyForPaint<typeof RovoDemandTestsPageType>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-rovo-demand-tests", jiraSpaEntry: "async-rovo-demand-tests" */ '@atlassian/jira-spa-apps-rovo-demand-tests'
		),
	),
);

export const RovoDemandTestsPage = () => (
	<LazyPage Page={LazyRovoDemandTestsPage} pageId="rovo-demand-tests" shouldShowSpinner={false} />
);

export const rovoDemandTestsRouteEntry: Route = createEntry(rovoDemandTestsRoute, () => ({
	resources: [
		...getBusinessProjectResources(),
		...getNavigationResources(),
		...getNavigationSidebarProjectResource(),
		projectContextResource,
		getConsolidationStateResource(),
	],

	component: componentWithCondition(
		getIsEnrolledInExperiment,
		RovoDemandTestsPage,
		ErrorPagesNotFound,
	),

	layout: genericProjectLayout,

	navigation: {
		menuId: MENU_ID.PROJECTS,
		sidebar: genericProjectSidebars,
		horizontal: GENERIC_PROJECT_HORIZONTAL_NAV,
	},

	forPaint: [LazyAtlassianNavigation, LazyRovoDemandTestsPage],
}));
